"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";

import { HandsClapping } from "@punchup/ui-common/components/common/icons";
import { cn } from "../common/utils";
import { formatClapCount } from "@/utils/formatters";
import { useAnimate } from "framer-motion";

export const MAX_USER_REACTIONS = 20;

export interface ReactionButtonProps {
  // State props
  totalCount: number;
  userCount: number;
  loading?: boolean;

  // UI props
  icon?: React.ComponentType<{
    width: number;
    height: number;
    className?: string;
  }>;
  size?: "sm" | "md" | "lg";
  className?: string;
  disabled?: boolean;

  // Animation props
  enableParticles?: boolean;
  animationStyle?: "simple" | "dynamic";

  // Callbacks
  onReaction: (count: number) => void;
  onClick?: () => void; // For custom click handling (e.g., auth modal)
}

const sizeConfigs = {
  sm: {
    button: "py-1 px-2 text-xs",
    icon: { width: 16, height: 16 },
    bubble: "w-8 h-8 text-xs",
    bubbleOffset: -20,
    fontSize: "text-xs",
    iconMargin: "",
  },
  md: {
    button: "py-[5px] md:py-1 px-2.5 md:px-2",
    icon: { width: 24, height: 24 },
    bubble: "w-10 h-10 text-sm",
    bubbleOffset: -20,
    fontSize: "text-[16px] md:text-lg",
    iconMargin: "-ml-[2px]",
  },
  lg: {
    button: "py-2 px-3",
    icon: { width: 32, height: 32 },
    bubble: "w-12 h-12 text-base",
    bubbleOffset: -25,
    fontSize: "text-lg",
    iconMargin: "",
  },
};

export function ReactionButton({
  totalCount,
  userCount,
  loading = false,
  icon: Icon = HandsClapping,
  size = "sm",
  className,
  disabled = false,
  enableParticles = true,
  animationStyle = "simple",
  onReaction,
  onClick,
}: ReactionButtonProps) {
  const [pendingCount, setPendingCount] = useState(0);
  const [scope, animate] = useAnimate();
  const [isHolding, setIsHolding] = useState(false);
  const holdTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const holdIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const clickHandledRef = useRef(false);
  const isTouchDevice = useRef(false);

  const config = sizeConfigs[size];
  const userTotal = userCount + pendingCount;
  const displayTotal = totalCount + pendingCount;
  const canReact = userTotal < MAX_USER_REACTIONS;

  // Debounced submission
  useEffect(() => {
    if (pendingCount > 0) {
      const timer = setTimeout(() => {
        onReaction(pendingCount);
        setPendingCount(0);
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [pendingCount, onReaction]);

  // Clean up timers on unmount
  useEffect(() => {
    return () => {
      if (holdTimeoutRef.current) clearTimeout(holdTimeoutRef.current);
      if (holdIntervalRef.current) clearInterval(holdIntervalRef.current);
    };
  }, []);

  const animateSimple = useCallback(() => {
    animate("button", { scale: [1, 1.1, 1] }, { duration: 0.2 });
    animate(
      ".reaction-bubble",
      {
        y: [0, -40],
        opacity: [0, 1, 0],
        scale: [0.8, 1, 0.8],
      },
      { duration: 0.6 }
    );

    // Add particles to simple animation too
    if (enableParticles) {
      for (let i = 0; i < 3; i++) {
        const angle = i * 120 - 60;
        const distance = 25 + i * 8; // Slightly smaller for simple style

        animate(
          `.particle-${i}`,
          {
            x: [0, Math.cos((angle * Math.PI) / 180) * distance],
            y: [0, -distance + Math.sin((angle * Math.PI) / 180) * 15],
            opacity: [0, 1, 0],
            scale: [0, 0.8, 0.4], // Slightly smaller particles
          },
          {
            duration: 0.5,
            delay: i * 0.04,
            ease: "easeOut",
          }
        );
      }
    }
  }, [animate, enableParticles]);

  const animateDynamic = useCallback(() => {
    // Button animation
    animate(
      "button",
      {
        scale: [1, 1.2, 0.95, 1.05, 1],
        rotate: [0, -5, 5, -2, 0],
      },
      { duration: 0.4, ease: "easeOut" }
    );

    // Icon animation
    animate(
      ".reaction-icon",
      {
        scale: [1, 1.3, 1],
        rotate: [0, -10, 10, 0],
      },
      { duration: 0.3, ease: "easeOut" }
    );

    // Bubble animation
    animate(
      ".reaction-bubble",
      {
        y: [-20, -40, -60],
        opacity: [0, 1, 0],
        scale: [0.5, 1.2, 0.8],
      },
      { duration: 0.8, ease: "easeOut" }
    );

    // Particles
    if (enableParticles) {
      for (let i = 0; i < 3; i++) {
        const angle = i * 120 - 60;
        const distance = 30 + i * 10;

        animate(
          `.particle-${i}`,
          {
            x: [0, Math.cos((angle * Math.PI) / 180) * distance],
            y: [0, -distance + Math.sin((angle * Math.PI) / 180) * 20],
            opacity: [0, 1, 0],
            scale: [0, 1, 0.5],
          },
          {
            duration: 0.6,
            delay: i * 0.05,
            ease: "easeOut",
          }
        );
      }
    }
  }, [animate, enableParticles]);

  const animateMaxedOut = useCallback(() => {
    // Special animation when maxed out
    animate(
      "button",
      {
        scale: [1, 1.1, 1],
        backgroundColor: ["", "rgba(239, 68, 68, 0.1)", ""],
      },
      { duration: 0.3 }
    );

    // Show max bubble with different styling
    animate(
      ".reaction-bubble",
      {
        y: [-20, -40],
        opacity: [0, 1, 0],
        scale: [0.8, 1.1, 0.8],
        backgroundColor: ["", "#ef4444", ""],
      },
      { duration: 0.6 }
    );

    // Add particles even when maxed out
    if (enableParticles) {
      for (let i = 0; i < 3; i++) {
        const angle = i * 120 - 60;
        const distance = 30 + i * 10;

        animate(
          `.particle-${i}`,
          {
            x: [0, Math.cos((angle * Math.PI) / 180) * distance],
            y: [0, -distance + Math.sin((angle * Math.PI) / 180) * 20],
            opacity: [0, 1, 0],
            scale: [0, 1, 0.5],
          },
          {
            duration: 0.6,
            delay: i * 0.05,
            ease: "easeOut",
          }
        );
      }
    }
  }, [animate, enableParticles]);

  const performReaction = useCallback(() => {
    if (canReact) {
      // Normal reaction
      if (animationStyle === "dynamic") {
        animateDynamic();
      } else {
        animateSimple();
      }
      setPendingCount((prev) => prev + 1);
    } else {
      // Maxed out - still animate but different style
      animateMaxedOut();
    }
  }, [
    canReact,
    animationStyle,
    animateDynamic,
    animateSimple,
    animateMaxedOut,
  ]);

  const startHolding = useCallback(
    (isTouchEvent = false) => {
      if (disabled || loading || onClick) return;

      clickHandledRef.current = true; // Prevent onClick from firing
      setIsHolding(true);

      // Immediate reaction on mouse down
      performReaction();

      // Use longer delays for touch events to reduce sensitivity
      const continuousDelay = isTouchEvent ? 800 : 600; // 800ms for touch, 600ms for mouse
      const continuousInterval = isTouchEvent ? 300 : 200; // 300ms for touch, 200ms for mouse

      // Start continuous reactions after delay
      holdTimeoutRef.current = setTimeout(() => {
        holdIntervalRef.current = setInterval(() => {
          performReaction();
        }, continuousInterval);
      }, continuousDelay);
    },
    [disabled, loading, onClick, performReaction]
  );

  const stopHolding = useCallback(() => {
    setIsHolding(false);
    if (holdTimeoutRef.current) {
      clearTimeout(holdTimeoutRef.current);
      holdTimeoutRef.current = null;
    }
    if (holdIntervalRef.current) {
      clearInterval(holdIntervalRef.current);
      holdIntervalRef.current = null;
    }

    // Reset click handled flag after a short delay
    setTimeout(() => {
      clickHandledRef.current = false;
    }, 50);
  }, []);

  const handleClick = (e: React.MouseEvent) => {
    if (disabled || loading) return;

    if (onClick) {
      onClick();
      return;
    }

    // Don't handle click if this is a touch device and touch already handled it
    if (isTouchDevice.current) {
      return;
    }

    // Only handle click if it wasn't already handled by mouse down
    if (!clickHandledRef.current) {
      performReaction();
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    isTouchDevice.current = true;
    startHolding(true);
  };

  const handleMouseDown = () => {
    // Only handle mouse down if we're not on a touch device
    if (!isTouchDevice.current) {
      startHolding(false);
    }
  };

  // Button color logic
  const getButtonColors = () => {
    if (userTotal > 0) {
      return size === "sm"
        ? "fill-primary text-primary bg-primary/6 hover:bg-primary/8 focus:bg-primary/10"
        : "fill-primary text-primary bg-primary/6 hover:bg-primary/8 focus:bg-primary/10";
    }
    return size === "sm"
      ? "fill-gray-500 text-gray-500 bg-gray-100 hover:bg-gray-200 focus:bg-gray-200"
      : "fill-primary-blue text-primary-blue bg-black/6 hover:bg-black/8 focus:bg-black/10";
  };

  return (
    <div className="relative w-fit" ref={scope}>
      <button
        className={cn(
          "relative z-20 group flex flex-row items-center gap-0.5 transition-colors duration-150 ease-in-out disabled:cursor-not-allowed select-none",
          size === "md" ? "rounded-md" : "rounded-full",
          config.button,
          getButtonColors(),
          loading && "animate-pulse duration-1000",
          isHolding && "scale-95",
          className
        )}
        onClick={handleClick}
        onMouseDown={handleMouseDown}
        onMouseUp={stopHolding}
        onMouseLeave={stopHolding}
        onTouchStart={handleTouchStart}
        onTouchEnd={stopHolding}
        onTouchCancel={stopHolding}
        disabled={disabled || loading}
        aria-disabled={disabled || loading}
        id={size === "md" ? "clap" : undefined}
      >
        <Icon
          width={config.icon.width}
          height={config.icon.height}
          className={cn(
            "reaction-icon transition-[fill] duration-150 ease-in-out fill-inherit",
            config.iconMargin
          )}
        />
        {loading ? (
          <div
            className={cn(
              "h-4",
              size === "sm" ? "w-6" : size === "md" ? "w-8 h-7" : "w-8"
            )}
          />
        ) : displayTotal > 0 ? (
          <span
            className={cn(
              "leading-none",
              size === "sm" ? "font-medium" : "font-semibold",
              size === "md" && "text-left",
              config.fontSize
            )}
          >
            {formatClapCount(displayTotal)}
          </span>
        ) : null}
      </button>

      {/* Reaction bubble */}
      <span
        className={cn(
          "reaction-bubble absolute z-10 flex items-center justify-center font-semibold text-white transform rounded-full opacity-0 select-none pointer-events-none",
          animationStyle === "dynamic"
            ? "bg-gradient-to-br from-purple-500 to-pink-500 shadow-lg font-bold"
            : "bg-primary",
          config.bubble
        )}
        style={{
          top: config.bubbleOffset,
          left: `calc(50% - ${parseInt(config.bubble.split(" ")[0].slice(2)) * 4}px)`,
        }}
      >
        +{userTotal >= MAX_USER_REACTIONS ? MAX_USER_REACTIONS : userTotal}
      </span>

      {/* Particles */}
      {enableParticles &&
        [0, 1, 2].map((i) => (
          <span
            key={i}
            className={`particle-${i} absolute z-10 w-2 h-2 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full opacity-0 pointer-events-none`}
            style={{
              top: -10,
              left: "calc(50% - 4px)",
            }}
          />
        ))}
    </div>
  );
}
